/**
 * DNS — widget panely (ModuleWorkspace + module-ui-catalog).
 * Zóny: live load z providera, export BIND, snapshoty, šablony.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { callWidgetTool } from '../../widgets/scope';
import type { WidgetRenderProps } from '../../widgets/types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { SearchInput } from '../../components/ui/SearchInput';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { RefreshButton } from '../../components/ui/RefreshButton';
import {
  CheckCircle2,
  Copy,
  Database,
  Download,
  FileText,
  Globe,
  History,
  KeyRound,
  Layers,
  Plus,
  Power,
  RefreshCw,
  Search,
  Server,
  Star,
  TestTube2,
  Trash2,
  Wand2,
} from 'lucide-react';
import { StatusDot } from '../../components/ui/StatusDot';
import { EmptyState } from '../../components/ui/EmptyState';
import { getModuleUi } from '../../widgets/module-ui-catalog';
import { ModuleWorkspace } from './ModuleWorkspace';
import { themedConfirm } from '../../lib/themed-dialogs';
import i18n from '../../lib/i18n';

interface DnsProvider {
  id: string;
  name: string;
  type: string;
  config?: Record<string, unknown>;
  enabled: boolean;
  priority: number;
}
interface DnsRecord {
  id: string;
  provider_name: string;
  domain: string;
  type: string;
  name: string;
  data: string;
  ttl: number;
  proxied: boolean;
  managed_by: string;
  desired: boolean;
  actual: boolean;
  last_sync: string | null;
}
interface LiveDnsRecord {
  provider_name: string;
  provider_type: string;
  provider_record_id: string | null;
  domain: string;
  type: string;
  name: string;
  fqdn: string;
  data: string;
  ttl: number;
  proxied: boolean;
  local_record_id?: string;
  desired?: boolean;
  actual?: boolean;
  managed_by?: string;
}

interface LiveZoneResponse {
  ok: boolean;
  error?: string;
  provider: string;
  provider_type: string;
  zone: string;
  total: number;
  local_total: number;
  records: LiveDnsRecord[];
}

function providerZones(config?: Record<string, unknown>): string[] {
  if (!config) return [];
  const zones: string[] = [];
  for (const key of ['zone', 'zone_name', 'domain']) {
    const value = config[key];
    if (typeof value === 'string' && value && value !== '***') zones.push(value);
  }
  for (const key of ['zones', 'domains']) {
    const value = config[key];
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item) zones.push(item);
      if (item && typeof item === 'object') {
        const rec = item as Record<string, unknown>;
        const name = rec.name ?? rec.zone ?? rec.domain;
        if (typeof name === 'string' && name) zones.push(name);
      }
    }
  }
  return [...new Set(zones)];
}

function providerTypeVariant(type: string) {
  if (type === 'domainmaster') return 'blue';
  if (type === 'cloudflare') return 'purple';
  if (type === 'wedos') return 'info';
  if (type === 'webglobe') return 'info';
  if (type === 'powerdns') return 'green';
  return 'gray';
}

function recordFqdn(record: DnsRecord) {
  return record.name ? `${record.name}.${record.domain}` : record.domain;
}

function Field({ label, value, onChange, mono, type: inputType }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  type?: string;
}) {
  return (
    <label className='block text-xs'>
      <span className='text-gray-400 mb-1 block'>{label}</span>
      <input
        type={inputType ?? 'text'}
        className={`w-full bg-gray-950/70 border border-gray-800 rounded-xl px-3 py-2 text-sm text-gray-100 outline-none transition-colors focus:border-blue-500/60 ${
          mono ? 'font-mono' : ''
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Widget: dns.providers */
export function DnsProvidersWidget(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scope = _props.scope ?? { node: null, identity: null };
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'domainmaster',
    username: '',
    password: '',
    api_token: '',
    zone_id: '',
    zone: '',
    billfirm: '',
  });

  const nodeKey = scope.node ?? 'local';
  const { data } = useQuery({
    queryKey: ['dns-providers', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; providers: DnsProvider[] }>(
        'dns.providers',
        { action: 'list' },
        scope,
      ),
  });
  const providers = data?.providers ?? [];
  const enabledCount = providers.filter((p) => p.enabled).length;
  const managedZones = providers.flatMap((p) => providerZones(p.config));

  const add = useMutation({
    mutationFn: () => {
      let config: Record<string, unknown>;
      if (form.type === 'domainmaster') {
        config = {
          username: form.username,
          password: form.password,
          ...(form.zone
            ? { domains: form.zone.split(',').map((z) => z.trim()).filter(Boolean) }
            : {}),
        };
      } else if (form.type === 'webglobe') {
        // Backend WebglobeConfig: login + api_token (API klíč s povolenou IP), volitelně billfirm
        config = {
          login: form.username,
          api_token: form.api_token,
          ...(form.billfirm.trim() ? { billfirm: form.billfirm.trim() } : {}),
          ...(form.zone
            ? { domains: form.zone.split(',').map((z) => z.trim()).filter(Boolean) }
            : {}),
        };
      } else if (form.type === 'wedos') {
        // Backend WedosConfig: username + wapi_password (ne account password)
        config = {
          username: form.username,
          wapi_password: form.password,
          ...(form.zone
            ? { domains: form.zone.split(',').map((z) => z.trim()).filter(Boolean) }
            : {}),
        };
      } else {
        config = {
          api_token: form.api_token,
          zone_id: form.zone_id,
          ...(form.zone ? { zone: form.zone } : {}),
        };
      }
      // name prázdné → backend vygeneruje (wedos-login)
      return callWidgetTool<{
        ok?: boolean;
        name?: string;
        error?: string;
        bootstrap?: {
          ok?: boolean;
          deferred?: boolean;
          error?: string;
          hint?: string;
          domains?: string[];
          snapshots?: number;
          records_total?: number;
        };
      }>('dns.providers', {
        action: 'add',
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
        type: form.type,
        config,
        bootstrap_after: true,
      }, scope);
    },
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('dns.providerAddFail', 'Přidání selhalo'));
        return;
      }
      const b = r?.bootstrap;
      if (b?.deferred || (b && b.ok === false)) {
        toast.success(
          t(
            'dns.providerSavedDeferred',
            'Provider {{name}} uložen. Import zón zatím nejde ({{why}}). Zkus později „Import + snapshot“.',
            {
              name: r?.name ?? (form.name || form.type),
              why: b.hint || b.error || 'API / allowlist',
            },
          ),
          { duration: 8000 },
        );
      } else if (b && (b.snapshots || b.records_total)) {
        toast.success(
          t(
            'dns.providerSavedBootstrap',
            'Provider {{name}}: {{zones}} zón, {{snaps}} snapshotů, {{recs}} záznamů',
            {
              name: r?.name ?? form.type,
              zones: b.domains?.length ?? 0,
              snaps: b.snapshots ?? 0,
              recs: b.records_total ?? 0,
            },
          ),
        );
      } else {
        toast.success(t('dns.providerPridan'));
      }
      setAdding(false);
      setForm({
        name: '',
        type: form.type,
        username: '',
        password: '',
        api_token: '',
        zone_id: '',
        zone: '',
        billfirm: '',
      });
      qc.invalidateQueries({ queryKey: ['dns-providers', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-provider-domains', nodeKey] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const test = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool<{ ok: boolean; error?: string; domains?: string[] }>(
        'dns.providers',
        { action: 'test', name },
        scope,
      ),
    onSuccess: (r, name) => {
      if (r.ok) {
        const n = r.domains?.length;
        toast.success(
          n != null
            ? t('dns.testOkZones', '{{name}}: OK ({{count}} zón)', { name, count: n })
            : t('dns.testOk', '{{name}}: OK', { name }),
        );
      } else {
        toast.error(`${name}: ${r.error}`, { duration: 8000 });
      }
    },
  });

  const bootstrap = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool<{
        ok?: boolean;
        deferred?: boolean;
        error?: string;
        hint?: string;
        domains?: string[];
        snapshots?: number;
        records_total?: number;
      }>('dns.provider_bootstrap', { provider_name: name }, scope),
    onSuccess: (r, name) => {
      if (r?.deferred || r?.ok === false) {
        toast.error(
          `${name}: ${r.hint || r.error || t('dns.importDeferred', 'import se odložil')}`,
          { duration: 9000 },
        );
      } else {
        toast.success(
          t(
            'dns.bootstrapOk',
            '{{name}}: {{zones}} zón, {{snaps}} snapshotů, {{recs}} záznamů',
            {
              name,
              zones: r.domains?.length ?? 0,
              snaps: r.snapshots ?? 0,
              recs: r.records_total ?? 0,
            },
          ),
        );
      }
      qc.invalidateQueries({ queryKey: ['dns-providers', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-provider-domains', nodeKey] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool('dns.providers', { action: 'delete', name }, scope),
    onSuccess: () => {
      toast.success(t('dns.smazano'));
      qc.invalidateQueries({ queryKey: ['dns-providers', nodeKey] });
    },
  });

  return (
    <div className='space-y-4'>
      <StatsGrid columns={3}>
        <StatCard icon={KeyRound} label={t('dns.provideri')} value={providers.length} sub={t('dns.autoritativniDnsUcty')} />
        <StatCard
          icon={CheckCircle2}
          label={t('dns.povolene')}
          value={enabledCount}
          sub={t('dns.dostupneProMcp')}
          variant={enabledCount ? 'success' : 'default'}
        />
        <StatCard
          icon={Globe}
          label={t('dns.zony')}
          value={managedZones.length}
          sub={managedZones.slice(0, 2).join(', ') || t('dns.noDescribedZone', 'bez popsané zóny')}
          variant='info'
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <div>
            <CardTitle icon={KeyRound}>{t('dns.dnsProvidery')}</CardTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('dns.uctyPresKtereMcp')}
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <RefreshButton
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dns-providers', nodeKey] })}
            />
            <Button variant='primary' size='sm' onClick={() => setAdding(true)}>
              <Plus size={12} /> {t('dns.pridat')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {providers.length === 0
            ? (
              <EmptyState
                icon={<Globe size={28} />}
                title={t('dns.zadnyDnsProvider')}
                description={t('dns.pridejDomainmasterNeboCloudfla')}
                action={(
                  <Button variant='primary' size='sm' onClick={() => setAdding(true)}>
                    <Plus size={12} /> {t('dns.pridatProvider')}
                  </Button>
                )}
              />
            )
            : (
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead className='text-xs text-gray-500 border-b border-gray-800'>
                    <tr>
                      <th className='text-left py-2'>{t('dns.nazev')}</th>
                      <th className='text-left py-2'>{t('dns.typ')}</th>
                      <th className='text-left py-2'>{t('dns.spravovaneZony')}</th>
                      <th className='text-center py-2'>{t('dns.priorita')}</th>
                      <th className='text-center py-2'>{t('dns.stav')}</th>
                      <th className='text-right py-2'>{t('dns.akce')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers.map((p) => {
                      const zones = providerZones(p.config);
                      return (
                        <tr key={p.id} className='border-b border-gray-800/40 last:border-0'>
                          <td className='py-2 pr-3'>
                            <div className='font-mono text-white text-xs'>{p.name}</div>
                          </td>
                          <td className='py-2 pr-3'>
                            <Badge
                              variant={providerTypeVariant(p.type) as never}
                              className='text-[10px]'
                            >
                              {p.type}
                            </Badge>
                          </td>
                          <td className='py-2 pr-3'>
                            {zones.length
                              ? (
                                <div className='flex flex-wrap gap-1'>
                                  {zones.slice(0, 4).map((zone) => (
                                    <Badge key={zone} variant='gray' className='font-mono text-[10px]'>
                                      {zone}
                                    </Badge>
                                  ))}
                                  {zones.length > 4 && <Badge variant='gray'>+{zones.length - 4}</Badge>}
                                </div>
                              )
                              : <span className='text-xs text-gray-600'>{t('dns.autoNezadane')}</span>}
                          </td>
                          <td className='py-2 text-center text-gray-400'>{p.priority}</td>
                          <td className='py-2 text-center'>
                            {p.enabled ? <Badge variant='green' dot>on</Badge> : <Badge variant='gray'>off</Badge>}
                          </td>
                          <td className='py-2'>
                            <div className='flex justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='icon'
                                title={t('dns.otestovatProvidera')}
                                onClick={() => test.mutate(p.name)}
                                loading={test.isPending && test.variables === p.name}
                              >
                                <TestTube2 size={13} />
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                title={t(
                                  'dns.bootstrapProvider',
                                  'Načíst zóny + live snapshot (i po allowlist delay)',
                                )}
                                onClick={() => bootstrap.mutate(p.name)}
                                loading={bootstrap.isPending && bootstrap.variables === p.name}
                              >
                                {t('dns.importSnapshot', 'Import + snapshot')}
                              </Button>
                              <Button
                                variant='ghost'
                                size='icon'
                                title={t('dns.smazatProvidera')}
                                onClick={() => del.mutate(p.name)}
                              >
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>

      {adding && (
        <Modal open onClose={() => setAdding(false)} title={t('dns.novyDnsProvider')}>
          <div className='space-y-3'>
            <label className='block text-xs'>
              <span className='text-gray-400 mb-1 block'>Typ</span>
              <Select
                className='w-full'
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value='domainmaster'>DomainMaster / K-NET</option>
                <option value='cloudflare'>Cloudflare</option>
                <option value='wedos'>WEDOS (WAPI)</option>
                <option value='webglobe'>{t('dns.providerWebglobe', 'Webglobe')}</option>
              </Select>
            </label>
            <Field
              label={t('dns.nazevSlugOptional', 'Název (volitelné — auto z typu/loginu)')}
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              mono
            />
            {form.type === 'webglobe'
              ? (
                <>
                  <Field
                    label={t('dns.webglobeLogin', 'Webglobe login (e-mail)')}
                    value={form.username}
                    onChange={(v) => setForm({ ...form, username: v })}
                  />
                  <Field
                    label={t('dns.webglobeApiKey', 'API klíč')}
                    value={form.api_token}
                    onChange={(v) => setForm({ ...form, api_token: v })}
                    type='password'
                    mono
                  />
                  <Field
                    label={t('dns.webglobeBillfirm', 'Billfirm (volitelné — jen u účtu jiné značky)')}
                    value={form.billfirm}
                    onChange={(v) => setForm({ ...form, billfirm: v })}
                    mono
                  />
                  <Field
                    label={t(
                      'dns.zonesOptional',
                      'Zóny (volitelné — načtou se z API po přidání)',
                    )}
                    value={form.zone}
                    onChange={(v) => setForm({ ...form, zone: v })}
                    mono
                  />
                  <p className='text-[11px] text-gray-500 leading-relaxed'>
                    {t(
                      'dns.webglobeHint',
                      'API klíč z administrace Webglobe (uživatel → API klíče). Klíč musí mít povolenou veřejnou IP tohoto uzlu, jinak Webglobe přihlášení odmítne.',
                    )}
                  </p>
                </>
              )
              : form.type === 'domainmaster' || form.type === 'wedos'
              ? (
                <>
                  <Field
                    label={form.type === 'wedos'
                      ? t('dns.wedosLogin', 'WEDOS login (e-mail)')
                      : 'Username'}
                    value={form.username}
                    onChange={(v) => setForm({ ...form, username: v })}
                  />
                  <Field
                    label={form.type === 'wedos'
                      ? t('dns.wedosWapiPassword', 'WAPI heslo')
                      : t('dns.apiPassword')}
                    value={form.password}
                    onChange={(v) => setForm({ ...form, password: v })}
                    type='password'
                  />
                  <Field
                    label={t(
                      'dns.zonesOptional',
                      'Zóny (volitelné — načtou se z API po přidání)',
                    )}
                    value={form.zone}
                    onChange={(v) => setForm({ ...form, zone: v })}
                    mono
                  />
                  {form.type === 'wedos' && (
                    <p className='text-[11px] text-gray-500 leading-relaxed'>
                      {t(
                        'dns.wedosHint',
                        'WAPI heslo z WEDOS → Nastavení → WAPI (ne panelové heslo). ' +
                          'IP ACM (veřejná) musí být v allowlistu — WEDOS to často propíše až po minutách. ' +
                          'Provider se uloží i když import teď selže; pak „Import + snapshot“.',
                      )}
                    </p>
                  )}
                </>
              )
              : (
                <>
                  <Field
                    label={t('dns.apiToken')}
                    value={form.api_token}
                    onChange={(v) => setForm({ ...form, api_token: v })}
                    type='password'
                    mono
                  />
                  <Field
                    label={t('dns.zoneId')}
                    value={form.zone_id}
                    onChange={(v) => setForm({ ...form, zone_id: v })}
                    mono
                  />
                  <Field
                    label={t('dns.zoneNameNaprNemovitostnik')}
                    value={form.zone}
                    onChange={(v) => setForm({ ...form, zone: v })}
                    mono
                  />
                </>
              )}
            <div className='flex justify-end gap-2 pt-2'>
              <Button variant='secondary' onClick={() => setAdding(false)}>{t('dns.zrusit')}</Button>
              <Button
                variant='primary'
                loading={add.isPending}
                disabled={
                  form.type === 'webglobe'
                    ? !form.username.trim() || !form.api_token.trim()
                    : form.type === 'wedos' || form.type === 'domainmaster'
                    ? !form.username.trim() || !form.password.trim()
                    : !form.api_token.trim() || !form.zone_id.trim()
                }
                onClick={() => add.mutate()}
              >
                {t('dns.pridat')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Widget: dns.zones — live zóna, import, export BIND, snapshoty */
export function DnsZonesWidget(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scope = _props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ domain: '', type: 'A', name: '', data: '', ttl: '1800' });
  // Prefill z URL ?zone=&provider= (odkaz z katalogu Domény)
  const urlParams = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : null;
  const [zoneDomain, setZoneDomain] = useState(urlParams?.get('zone') ?? '');
  const [providerName, setProviderName] = useState(urlParams?.get('provider') ?? '');
  const [autoLoad, setAutoLoad] = useState(urlParams?.get('zone') ? 1 : 0);
  const [snapNote, setSnapNote] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importBackup, setImportBackup] = useState(true);
  const [viewVersionId, setViewVersionId] = useState<string | null>(null);
  const [restoreKeys, setRestoreKeys] = useState<Set<string>>(new Set());
  const [restoreBackup, setRestoreBackup] = useState(true);

  const { data } = useQuery({
    queryKey: ['dns-records', nodeKey],
    queryFn: () => callWidgetTool<{ ok: boolean; records: DnsRecord[] }>('dns.list', {}, scope),
  });
  const records = data?.records ?? [];
  const providersQ = useQuery({
    queryKey: ['dns-providers', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; providers: DnsProvider[] }>(
        'dns.providers',
        { action: 'list' },
        scope,
      ),
  });
  const providers = providersQ.data?.providers ?? [];
  const knownZones = useMemo(() => {
    const fromProv = providers.flatMap((p) => providerZones(p.config));
    const fromLocal = records.map((r) => r.domain);
    return [...new Set([...fromProv, ...fromLocal])].filter(Boolean).sort();
  }, [providers, records]);

  const liveQ = useQuery({
    queryKey: ['dns-live-zone', nodeKey, zoneDomain, providerName, autoLoad],
    enabled: !!(zoneDomain || providerName) && autoLoad > 0,
    queryFn: () =>
      callWidgetTool<LiveZoneResponse>('dns.zone_records', {
        ...(zoneDomain ? { domain: zoneDomain } : {}),
        ...(providerName ? { provider_name: providerName } : {}),
      }, scope),
  });
  const live = liveQ.data?.ok ? liveQ.data : null;
  const syncedRecords = records.filter((r) => r.actual).length;
  const pendingRecords = records.length - syncedRecords;
  const zones = [...new Set(records.map((r) => r.domain))];

  const versionsQ = useQuery({
    queryKey: ['dns-zone-versions', nodeKey, zoneDomain],
    enabled: !!zoneDomain,
    queryFn: () =>
      callWidgetTool<{
        ok: boolean;
        versions?: Array<{
          id: string;
          source: string;
          note: string | null;
          record_count: number;
          created_at: string;
          provider_name: string;
        }>;
      }>('dns.zone_versions', {
        action: 'list',
        domain: zoneDomain,
        ...(providerName ? { provider_name: providerName } : {}),
      }, scope, { silent: true }),
  });
  const versions = versionsQ.data?.versions ?? [];

  const loadLive = () => setAutoLoad((n) => n + 1);

  const add = useMutation({
    mutationFn: () =>
      callWidgetTool('dns.create', { ...form, ttl: parseInt(form.ttl, 10) }, scope),
    onSuccess: () => {
      toast.success(t('dns.zaznamVytvoren'));
      setAdding(false);
      qc.invalidateQueries({ queryKey: ['dns-records', nodeKey] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => callWidgetTool('dns.delete', { id }, scope),
    onSuccess: () => {
      toast.success(t('dns.smazano'));
      qc.invalidateQueries({ queryKey: ['dns-records', nodeKey] });
    },
  });

  const sync = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok: boolean; created: number; deleted: number; failed: number }>(
        'dns.sync',
        {},
        scope,
      ),
    onSuccess: (r) =>
      toast.success(
        t('dns.syncVysledek', {
          created: r.created,
          deleted: r.deleted,
          failed: r.failed,
        }),
      ),
  });
  const pull = useMutation({
    mutationFn: (backupBefore: boolean) =>
      callWidgetTool<{
        ok: boolean;
        error?: string;
        total: number;
        inserted: number;
        updated: number;
        backup_snapshot_id?: string | null;
      }>(
        'dns.zone_pull',
        {
          ...(zoneDomain ? { domain: zoneDomain } : {}),
          ...(providerName ? { provider_name: providerName } : {}),
          backup_before: backupBefore,
        },
        scope,
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('dns.importZonySelhal'));
        return;
      }
      const bak = r.backup_snapshot_id
        ? ` ${t('dns.zalohaId', { id: r.backup_snapshot_id.slice(0, 8) })}`
        : '';
      toast.success(
        `${t('dns.importZonyOk', { inserted: r.inserted, updated: r.updated })}${bak}`,
      );
      setImportOpen(false);
      qc.invalidateQueries({ queryKey: ['dns-records', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-zone-versions', nodeKey, zoneDomain] });
      loadLive();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exportMut = useMutation({
    mutationFn: (opts: { source: 'live' | 'local' | 'version'; versionId?: string }) =>
      callWidgetTool<{
        ok: boolean;
        error?: string;
        text?: string;
        filename?: string;
      }>('dns.zone_export', {
        source: opts.source,
        ...(zoneDomain ? { domain: zoneDomain } : {}),
        ...(providerName ? { provider_name: providerName } : {}),
        ...(opts.versionId ? { version_id: opts.versionId } : {}),
      }, scope),
    onSuccess: (r) => {
      if (!r.ok || !r.text) {
        toast.error(r.error ?? t('dns.exportSelhal'));
        return;
      }
      downloadText(r.filename ?? 'zone.zone', r.text);
      toast.success(t('dns.exportHotovo'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const snapMut = useMutation({
    mutationFn: (source: 'live' | 'local') =>
      callWidgetTool<{ ok: boolean; error?: string; id?: string; record_count?: number }>(
        'dns.zone_versions',
        {
          action: 'snapshot',
          source,
          domain: zoneDomain || live?.zone,
          ...(providerName ? { provider_name: providerName } : {}),
          note: snapNote || undefined,
        },
        scope,
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('dns.snapshotSelhal'));
        return;
      }
      toast.success(t('dns.snapshotUlozen', { count: r.record_count ?? 0 }));
      setSnapNote('');
      qc.invalidateQueries({ queryKey: ['dns-zone-versions', nodeKey, zoneDomain] });
    },
  });

  const versionDetailQ = useQuery({
    queryKey: ['dns-zone-version', nodeKey, viewVersionId],
    enabled: !!viewVersionId,
    queryFn: () =>
      callWidgetTool<{
        ok: boolean;
        error?: string;
        version?: {
          id: string;
          zone: string;
          provider_name: string;
          source: string;
          note: string | null;
          records: Array<{ type: string; name: string; data: string; ttl?: number; fqdn?: string }>;
          bind_text?: string | null;
          created_at: string;
          record_count: number;
        };
      }>('dns.zone_versions', { action: 'get', version_id: viewVersionId }, scope),
  });
  const versionDetail = versionDetailQ.data?.version;
  const versionRecs = versionDetail?.records ?? [];

  const restoreMut = useMutation({
    mutationFn: (opts: { all: boolean }) =>
      callWidgetTool<{
        ok: boolean;
        error?: string;
        restored?: number;
        backup_snapshot_id?: string | null;
        note?: string;
      }>('dns.zone_versions', {
        action: 'restore',
        version_id: viewVersionId,
        backup_before: restoreBackup,
        ...(opts.all ? {} : { record_keys: [...restoreKeys] }),
        ...(providerName ? { provider_name: providerName } : {}),
      }, scope),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('dns.obnovaSelhala'));
        return;
      }
      const bak = r.backup_snapshot_id
        ? ` ${t('dns.zalohaId', { id: r.backup_snapshot_id.slice(0, 8) })}`
        : '';
      toast.success(
        `${t('dns.obnovenoZaznamu', { count: r.restored ?? 0 })}${bak}${r.note ? ` ${r.note}` : ''}`,
      );
      setViewVersionId(null);
      setRestoreKeys(new Set());
      qc.invalidateQueries({ queryKey: ['dns-records', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-zone-versions', nodeKey, zoneDomain] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const recKey = (r: { type: string; name: string; data: string }) =>
    `${String(r.type).toUpperCase()}|${r.name ?? ''}|${r.data ?? ''}`;

  return (
    <div className='space-y-4 p-1 min-w-0'>
      <StatsGrid columns={4} className='gap-2 sm:gap-4'>
        <StatCard
          icon={Database}
          label={t('dns.lokalniZaznamy')}
          value={records.length}
          sub={`${zones.length} ${t('dns.zon')}`}
          className='p-3 sm:p-5'
        />
        <StatCard
          icon={CheckCircle2}
          label={t('dns.synchronni')}
          value={syncedRecords}
          sub='actual=true'
          variant='success'
          className='p-3 sm:p-5'
        />
        <StatCard
          icon={RefreshCw}
          label={t('dns.ceka')}
          value={pendingRecords}
          sub={t('dns.pendingSync')}
          variant={pendingRecords ? 'warning' : 'default'}
          className='p-3 sm:p-5'
        />
        <StatCard
          icon={Layers}
          label={t('dns.liveZona')}
          value={live?.total ?? '—'}
          sub={live?.zone ?? t('dns.nenacteno')}
          variant='info'
          className='p-3 sm:p-5'
        />
      </StatsGrid>

      <Card>
        <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
          <div className='min-w-0 flex-1'>
            <CardTitle icon={Globe}>{t('dns.liveZonaZProvidera')}</CardTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('dns.cteSkutecnyStavPrimo')}
            </p>
          </div>
          {live && <Badge variant='blue'>{live.total} live</Badge>}
        </CardHeader>
        <CardContent className='min-w-0'>
          {knownZones.length > 0 && (
            <div className='flex flex-wrap gap-1.5 mb-3'>
              {knownZones.map((z) => (
                <button
                  key={z}
                  type='button'
                  onClick={() => {
                    setZoneDomain(z);
                    setAutoLoad((n) => n + 1);
                  }}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] font-mono transition-colors max-w-full truncate ${
                    zoneDomain === z
                      ? 'border-blue-500/40 bg-blue-500/15 text-blue-300'
                      : 'border-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-200'
                  }`}
                >
                  {z}
                </button>
              ))}
            </div>
          )}
          <div className='grid grid-cols-1 md:grid-cols-[minmax(140px,200px)_1fr] lg:grid-cols-[minmax(160px,220px)_1fr_auto] gap-2 items-end'>
            <label className='block text-xs min-w-0'>
              <span className='text-gray-400 mb-1 block'>{t('dns.provider')}</span>
              <Select
                className='w-full'
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
              >
                <option value=''>{t('dns.autoPodleDomeny')}</option>
                {providers.map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </Select>
            </label>
            <Field
              label={t('dns.domenaZona')}
              value={zoneDomain}
              onChange={setZoneDomain}
              mono
            />
            <div className='grid grid-cols-2 sm:flex sm:flex-wrap gap-2 lg:col-span-1 md:col-span-2 lg:col-auto'>
              <Button
                variant='secondary'
                size='sm'
                className='w-full sm:w-auto justify-center'
                disabled={!zoneDomain && !providerName}
                loading={liveQ.isFetching}
                onClick={loadLive}
              >
                <Search size={12} /> {t('dns.nacist')}
              </Button>
              <Button
                variant='primary'
                size='sm'
                className='w-full sm:w-auto justify-center'
                disabled={(!zoneDomain && !providerName) || !live}
                loading={pull.isPending}
                onClick={() => {
                  setImportBackup(true);
                  setImportOpen(true);
                }}
                title={t('dns.importLiveDoAcm')}
              >
                <Download size={12} /> {t('dns.importDoAcm')}
              </Button>
              <Button
                variant='secondary'
                size='sm'
                className='w-full sm:w-auto justify-center'
                disabled={!zoneDomain && !live}
                loading={exportMut.isPending}
                onClick={() => exportMut.mutate({ source: 'live' })}
                title={t('dns.stahnoutZoneLive')}
              >
                <FileText size={12} /> {t('dns.stahnoutZone')}
              </Button>
              <Button
                variant='secondary'
                size='sm'
                className='w-full sm:w-auto justify-center'
                disabled={!zoneDomain}
                loading={snapMut.isPending}
                onClick={() => snapMut.mutate('live')}
                title={t('dns.ulozitSnapshotLive')}
              >
                <History size={12} /> {t('dns.snapshot')}
              </Button>
            </div>
          </div>
          <div className='mt-2'>
            <Field
              label={t('dns.snapshotNote')}
              value={snapNote}
              onChange={setSnapNote}
            />
          </div>

          {liveQ.data && !liveQ.data.ok && (
            <div className='mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl p-3'>
              {liveQ.data.error ?? t('dns.liveCteniSelhalo')}
            </div>
          )}

          {live && (
            <div className='mt-4 min-w-0'>
              <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
                <div className='text-xs text-gray-400 min-w-0 break-all'>
                  <span className='font-mono text-gray-200'>{live.zone}</span>
                  {' '}{t('dns.pres')}{' '}
                  <span className='font-mono'>{live.provider}</span>
                </div>
                <div className='flex items-center gap-1.5 shrink-0'>
                  <Badge variant='gray'>{live.provider_type}</Badge>
                  <Badge variant='green'>{live.local_total} {t('dns.vAcm')}</Badge>
                </div>
              </div>
              {/* Mobile: karty */}
              <div className='md:hidden max-h-[min(24rem,50vh)] overflow-auto space-y-2'>
                {live.records.map((r) => (
                  <div
                    key={`${r.provider_record_id ?? ''}:${r.type}:${r.fqdn}:${r.data}`}
                    className='rounded-xl border border-gray-800 bg-gray-950/50 p-2.5 space-y-1'
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <Badge variant='gray' className='text-[10px]'>{r.type}</Badge>
                      {r.local_record_id
                        ? <Badge variant={r.actual ? 'green' : 'yellow'}>{t('dns.tracked')}</Badge>
                        : <Badge variant='gray'>{t('dns.liveOnly')}</Badge>}
                    </div>
                    <div className='font-mono text-xs text-gray-200 break-all'>{r.fqdn}</div>
                    <div className='font-mono text-[11px] text-gray-400 break-all'>{r.data}</div>
                    <div className='text-[10px] text-gray-600'>TTL {r.ttl}</div>
                  </div>
                ))}
              </div>
              {/* Desktop: tabulka */}
              <div className='hidden md:block max-h-[360px] overflow-auto border border-gray-800 rounded-xl'>
                <table className='w-full text-xs'>
                  <thead className='text-gray-500 border-b border-gray-800 sticky top-0 bg-gray-950'>
                    <tr>
                      <th className='text-left p-2'>FQDN</th>
                      <th className='text-left p-2'>{t('dns.typ')}</th>
                      <th className='text-left p-2'>{t('dns.hodnota')}</th>
                      <th className='text-left p-2'>TTL</th>
                      <th className='text-left p-2'>ACM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.records.map((r) => (
                      <tr key={`${r.provider_record_id ?? ''}:${r.type}:${r.fqdn}:${r.data}`} className='border-b border-gray-800/40 last:border-0'>
                        <td className='p-2 font-mono text-gray-200'>{r.fqdn}</td>
                        <td className='p-2'><Badge variant='gray' className='text-[10px]'>{r.type}</Badge></td>
                        <td className='p-2 font-mono text-gray-400 max-w-[360px] truncate' title={r.data}>{r.data}</td>
                        <td className='p-2 text-gray-500'>{r.ttl}</td>
                        <td className='p-2'>
                          {r.local_record_id
                            ? <Badge variant={r.actual ? 'green' : 'yellow'}>{t('dns.tracked')}</Badge>
                            : <Badge variant='gray'>{t('dns.liveOnly')}</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {zoneDomain && (
            <div className='mt-4 border border-gray-800 rounded-xl p-3 space-y-2 min-w-0'>
              <div className='text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5'>
                <History size={12} /> {t('dns.verzeZony')} ({versions.length})
              </div>
              {versions.length === 0 && (
                <p className='text-[11px] text-gray-600'>
                  {t('dns.zadneSnapshoty')}
                </p>
              )}
              <div className='max-h-56 overflow-auto space-y-1.5'>
                {versions.map((v) => (
                  <div
                    key={v.id}
                    className='flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 text-[11px] rounded-lg bg-gray-950/60 px-2.5 py-2'
                  >
                    <div className='flex flex-wrap items-center gap-1.5 min-w-0 flex-1'>
                      <span className='font-mono text-gray-500 shrink-0'>{v.id.slice(0, 8)}</span>
                      <Badge variant='gray'>{v.source}</Badge>
                      <span className='text-gray-400'>{v.record_count} {t('dns.rec')}</span>
                      <span className='text-gray-600 truncate max-w-full sm:flex-1'>{v.note || '—'}</span>
                      <span className='text-gray-600 shrink-0 text-[10px] sm:text-[11px]'>
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className='flex gap-1 shrink-0 self-end sm:self-auto'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setViewVersionId(v.id);
                          setRestoreKeys(new Set());
                          setRestoreBackup(true);
                        }}
                        title={t('dns.prohlizetSnapshot')}
                      >
                        <Search size={12} />
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => exportMut.mutate({ source: 'version', versionId: v.id })}
                        title={t('dns.stahnoutZoneZeSnapshotu')}
                      >
                        <FileText size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Potvrzení importu do ACM */}
      {importOpen && (
        <Modal
          open
          onClose={() => setImportOpen(false)}
          title={t('dns.importConfirmTitle')}
        >
          <div className='space-y-3 text-sm text-gray-300'>
            <p>{t('dns.importConfirmBody')}</p>
            <p className='font-mono text-xs text-gray-400 break-all'>
              {live?.zone ?? zoneDomain}
              {live?.provider ? ` ← ${live.provider}` : ''}
              {live ? ` · ${live.total} ${t('dns.zaznamu')}` : ''}
            </p>
            <label className='flex items-start gap-2 text-xs text-gray-400 cursor-pointer'>
              <input
                type='checkbox'
                className='mt-0.5 shrink-0'
                checked={importBackup}
                onChange={(e) => setImportBackup(e.target.checked)}
              />
              <span>{t('dns.backupBeforeImport')}</span>
            </label>
            <div className='flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-800'>
              <Button variant='secondary' className='w-full sm:w-auto' onClick={() => setImportOpen(false)}>
                {t('dns.zrusit')}
              </Button>
              <Button
                variant='primary'
                className='w-full sm:w-auto'
                loading={pull.isPending}
                onClick={() => pull.mutate(importBackup)}
              >
                {t('dns.importDoAcm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Prohlížení snapshotu + restore */}
      {viewVersionId && (
        <Modal
          open
          size='xl'
          onClose={() => {
            setViewVersionId(null);
            setRestoreKeys(new Set());
          }}
          title={t('dns.snapshotDetail')}
        >
          <div className='space-y-3 min-w-0'>
            {versionDetailQ.isLoading && (
              <div className='text-sm text-gray-500'>{t('dns.nacitam')}</div>
            )}
            {versionDetail && (
              <>
                <div className='flex flex-wrap gap-2 text-xs text-gray-400'>
                  <span className='font-mono text-gray-200 break-all'>{versionDetail.zone}</span>
                  <Badge variant='gray'>{versionDetail.source}</Badge>
                  <span>{versionDetail.record_count} {t('dns.rec')}</span>
                  <span className='font-mono text-gray-600'>{versionDetail.id.slice(0, 8)}</span>
                  <span>{new Date(versionDetail.created_at).toLocaleString()}</span>
                </div>
                {versionDetail.note && (
                  <p className='text-xs text-gray-500'>{versionDetail.note}</p>
                )}
                <div className='grid grid-cols-1 sm:flex sm:flex-wrap gap-2'>
                  <Button
                    size='sm'
                    variant='secondary'
                    className='w-full sm:w-auto justify-center'
                    onClick={() =>
                      exportMut.mutate({ source: 'version', versionId: versionDetail.id })}
                  >
                    <FileText size={12} /> {t('dns.stahnoutZone')}
                  </Button>
                  <Button
                    size='sm'
                    variant='secondary'
                    className='w-full sm:w-auto justify-center'
                    onClick={() =>
                      setRestoreKeys(new Set(versionRecs.map((r) => recKey(r))))}
                  >
                    {t('dns.vybratVse')}
                  </Button>
                  <Button
                    size='sm'
                    variant='ghost'
                    className='w-full sm:w-auto justify-center'
                    onClick={() => setRestoreKeys(new Set())}
                  >
                    {t('dns.zrusitVyber')}
                  </Button>
                </div>
                {/* Mobile karty výběru */}
                <div className='md:hidden max-h-[min(18rem,45vh)] overflow-auto space-y-1.5'>
                  {versionRecs.map((r) => {
                    const k = recKey(r);
                    const on = restoreKeys.has(k);
                    return (
                      <button
                        key={k}
                        type='button'
                        className={`w-full text-left rounded-xl border p-2.5 space-y-0.5 ${
                          on
                            ? 'border-blue-500/40 bg-blue-500/10'
                            : 'border-gray-800 bg-gray-950/40'
                        }`}
                        onClick={() => {
                          setRestoreKeys((prev) => {
                            const n = new Set(prev);
                            if (n.has(k)) n.delete(k);
                            else n.add(k);
                            return n;
                          });
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <input type='checkbox' checked={on} readOnly className='shrink-0' />
                          <Badge variant='gray'>{r.type}</Badge>
                          <span className='font-mono text-xs text-gray-300 truncate'>{r.name || '@'}</span>
                        </div>
                        <div className='font-mono text-[11px] text-gray-400 break-all pl-6'>{r.data}</div>
                      </button>
                    );
                  })}
                </div>
                <div className='hidden md:block max-h-[min(22rem,50vh)] overflow-auto border border-gray-800 rounded-xl'>
                  <table className='w-full text-xs'>
                    <thead className='text-gray-500 border-b border-gray-800 sticky top-0 bg-gray-950'>
                      <tr>
                        <th className='p-2 w-8' />
                        <th className='text-left p-2'>{t('dns.typ')}</th>
                        <th className='text-left p-2'>{t('dns.jmeno')}</th>
                        <th className='text-left p-2'>{t('dns.data')}</th>
                        <th className='text-left p-2'>TTL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versionRecs.map((r) => {
                        const k = recKey(r);
                        const on = restoreKeys.has(k);
                        return (
                          <tr
                            key={k}
                            className={`border-b border-gray-800/40 cursor-pointer ${
                              on ? 'bg-blue-500/10' : ''
                            }`}
                            onClick={() => {
                              setRestoreKeys((prev) => {
                                const n = new Set(prev);
                                if (n.has(k)) n.delete(k);
                                else n.add(k);
                                return n;
                              });
                            }}
                          >
                            <td className='p-2 text-center'>
                              <input type='checkbox' checked={on} readOnly />
                            </td>
                            <td className='p-2'><Badge variant='gray'>{r.type}</Badge></td>
                            <td className='p-2 font-mono text-gray-300'>{r.name || '@'}</td>
                            <td className='p-2 font-mono text-gray-400 max-w-[280px] truncate' title={r.data}>
                              {r.data}
                            </td>
                            <td className='p-2 text-gray-500'>{r.ttl ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <label className='flex items-start gap-2 text-xs text-gray-400 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='mt-0.5 shrink-0'
                    checked={restoreBackup}
                    onChange={(e) => setRestoreBackup(e.target.checked)}
                  />
                  <span>{t('dns.backupBeforeRestore')}</span>
                </label>
                <div className='flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2 pt-2 border-t border-gray-800'>
                  <Button
                    variant='secondary'
                    className='w-full sm:w-auto'
                    onClick={() => {
                      setViewVersionId(null);
                      setRestoreKeys(new Set());
                    }}
                  >
                    {t('dns.zavrit')}
                  </Button>
                  <Button
                    variant='secondary'
                    className='w-full sm:w-auto'
                    loading={restoreMut.isPending}
                    disabled={restoreKeys.size === 0}
                    onClick={() => restoreMut.mutate({ all: false })}
                  >
                    {t('dns.obnovitVybrane')} ({restoreKeys.size})
                  </Button>
                  <Button
                    variant='primary'
                    className='w-full sm:w-auto'
                    loading={restoreMut.isPending}
                    onClick={() => restoreMut.mutate({ all: true })}
                  >
                    {t('dns.obnovitCely')}
                  </Button>
                </div>
                <p className='text-[10px] text-gray-600'>{t('dns.restoreHint')}</p>
              </>
            )}
          </div>
        </Modal>
      )}

      <Card>
        <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
          <div className='min-w-0 flex-1'>
            <CardTitle icon={Database}>{t('dns.lokalniAcmStav')}</CardTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('dns.desiredActualZaznamyKtere')}
            </p>
          </div>
          <div className='grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto'>
            <Button
              variant='secondary'
              size='sm'
              className='w-full sm:w-auto justify-center'
              onClick={() => sync.mutate()}
              loading={sync.isPending}
            >
              <RefreshCw size={12} /> Sync
            </Button>
            <Button
              variant='secondary'
              size='sm'
              className='w-full sm:w-auto justify-center'
              disabled={!zoneDomain}
              onClick={() => exportMut.mutate({ source: 'local' })}
              title={t('dns.stahnoutZoneLocal')}
            >
              <FileText size={12} /> {t('dns.stahnoutZoneLocalBtn')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              className='w-full sm:w-auto justify-center col-span-2 sm:col-span-1'
              onClick={() => setAdding(true)}
            >
              <Plus size={12} /> {t('dns.novy')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className='min-w-0'>
          {records.length === 0
            ? (
              <EmptyState
                icon={<Database size={28} />}
                title={t('dns.zadneLokalniDnsZaznamy')}
                description={t('dns.nactiLiveZonuZ')}
              />
            )
            : (
              <>
                <div className='md:hidden space-y-2'>
                  {records.map((r) => (
                    <div
                      key={r.id}
                      className='rounded-xl border border-gray-800 bg-gray-950/50 p-2.5 space-y-1'
                    >
                      <div className='flex items-center justify-between gap-2'>
                        <Badge variant='gray' className='text-[10px]'>{r.type}</Badge>
                        <div className='flex items-center gap-1'>
                          {r.actual
                            ? <Badge variant='green'>{t('dns.synced')}</Badge>
                            : <Badge variant='yellow'>{t('dns.pending')}</Badge>}
                          <Button
                            variant='ghost'
                            size='icon'
                            title={t('dns.smazatZaznam')}
                            onClick={() => del.mutate(r.id)}
                          >
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </div>
                      <div className='font-mono text-xs text-white break-all'>{recordFqdn(r)}</div>
                      <div className='font-mono text-[11px] text-gray-400 break-all'>{r.data}</div>
                      <div className='text-[10px] text-gray-600 flex flex-wrap gap-x-2'>
                        <span>TTL {r.ttl}</span>
                        <span>{r.provider_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className='hidden md:block overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead className='text-xs text-gray-500 border-b border-gray-800'>
                      <tr>
                        <th className='text-left py-2'>FQDN</th>
                        <th className='text-left py-2'>{t('dns.typ')}</th>
                        <th className='text-left py-2'>{t('dns.hodnota')}</th>
                        <th className='text-left py-2'>TTL</th>
                        <th className='text-left py-2'>{t('dns.provider')}</th>
                        <th className='text-center py-2'>{t('dns.stav')}</th>
                        <th className='text-right py-2'>{t('dns.akce')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr key={r.id} className='border-b border-gray-800/40 last:border-0'>
                          <td className='py-2 pr-3 text-white font-mono text-xs'>{recordFqdn(r)}</td>
                          <td className='py-2 pr-3'>
                            <Badge variant='gray' className='text-[10px]'>{r.type}</Badge>
                          </td>
                          <td className='py-2 pr-3 text-gray-400 font-mono text-xs max-w-[320px] truncate' title={r.data}>
                            {r.data}
                          </td>
                          <td className='py-2 pr-3 text-gray-500 text-xs'>{r.ttl}</td>
                          <td className='py-2 pr-3 text-gray-500 text-xs'>{r.provider_name}</td>
                          <td className='py-2 text-center'>
                            {r.actual
                              ? <Badge variant='green'>{t('dns.synced')}</Badge>
                              : <Badge variant='yellow'>{t('dns.pending')}</Badge>}
                          </td>
                          <td className='py-2'>
                            <div className='flex justify-end'>
                              <Button
                                variant='ghost'
                                size='icon'
                                title={t('dns.smazatZaznam')}
                                onClick={() => del.mutate(r.id)}
                              >
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </CardContent>
      </Card>

      {adding && (
        <Modal open onClose={() => setAdding(false)} title={t('dns.novyDnsZaznam')}>
          <div className='space-y-3'>
            <Field
              label={t('dns.domena')}
              value={form.domain}
              onChange={(v) => setForm({ ...form, domain: v })}
              mono
            />
            <label className='block text-xs'>
              <span className='text-gray-400 mb-1 block'>{t('dns.typ')}</span>
              <Select
                className='w-full'
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {['A', 'CNAME', 'TXT', 'MX', 'AAAA', 'SRV', 'CAA'].map((tt) => (
                  <option key={tt} value={tt}>{tt}</option>
                ))}
              </Select>
            </label>
            <Field
              label={t('dns.nazevSubdomenaPrazdneRoot')}
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              mono
            />
            <Field
              label={t('dns.hodnotaIpHostnameText')}
              value={form.data}
              onChange={(v) => setForm({ ...form, data: v })}
              mono
            />
            <Field
              label={t('dns.ttlSekundy')}
              value={form.ttl}
              onChange={(v) => setForm({ ...form, ttl: v })}
            />
            <div className='flex justify-end gap-2 pt-2'>
              <Button variant='secondary' onClick={() => setAdding(false)}>{t('dns.zrusit')}</Button>
              <Button variant='primary' loading={add.isPending} onClick={() => add.mutate()}>
                {t('dns.vytvorit')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── DNS Server (AdGuard Home) Tab ──────────────────────────────────────────
interface DnsServerStatus {
  ok: boolean;
  local: {
    running: boolean;
    container_name: string;
    container_status: string | null;
    admin_api_alive: boolean;
    admin_url_local: string;
    advertised: boolean;
    dns_port: number | null;
    public_ip: string | null;
  };
  mesh: {
    servers: Array<{
      node_id: string;
      hostname?: string;
      public_ip?: string;
      dns_port?: number;
      dns_url_udp?: string;
      admin_url_local?: string;
      container_name?: string;
      engine?: string;
      capabilities?: string[];
    }>;
    count: number;
  };
}

export function DnsServerWidget(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scope = _props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';
  const [enableOpen, setEnableOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [dnsPort, setDnsPort] = useState(53);
  const [purgeData, setPurgeData] = useState(false);

  const statusQ = useQuery({
    queryKey: ['dns-server-status', nodeKey],
    queryFn: () => callWidgetTool<DnsServerStatus>('dns.server_status', {}, scope),
    refetchInterval: 10_000,
  });

  const enableMut = useMutation({
    mutationFn: (port: number) =>
      callWidgetTool('dns.server_enable', { dns_port: port }, scope),
    onSuccess: (data: { ok: boolean; first_boot?: string; note?: string; admin_password?: string | null; error?: string }) => {
      if (data.ok) {
        toast.success(data.note ?? 'DNS server enabled');
        if (data.admin_password) {
          navigator.clipboard.writeText(data.admin_password).catch(() => {});
          toast.info(t('dns.adminPasswordCopied', 'Admin heslo zkopírováno do schránky: {{password}}', { password: data.admin_password }), { duration: 30_000 });
        }
      } else {
        toast.error(data.error ?? i18n.t('dns.enableSelhalo', 'Enable selhalo'));
      }
      qc.invalidateQueries({ queryKey: ['dns-server-status', nodeKey] });
      setEnableOpen(false);
    },
  });

  const disableMut = useMutation({
    mutationFn: (purge: boolean) =>
      callWidgetTool('dns.server_disable', { purge_data: purge }, scope),
    onSuccess: (data: { ok: boolean; error?: string }) => {
      if (data.ok) toast.success(t('dns.dnsServerVypnut'));
      else toast.error(data.error ?? i18n.t('dns.disableSelhalo', 'Disable selhalo'));
      qc.invalidateQueries({ queryKey: ['dns-server-status', nodeKey] });
      setDisableOpen(false);
    },
  });

  const s = statusQ.data;
  const localRunning = !!s?.local.running;
  const adminAlive = !!s?.local.admin_api_alive;

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Server} className='text-sm'>{t('dns.lokalniDnsServerAdguard')}</CardTitle>
        </CardHeader>
        <CardContent>
          {!s ? <div className='text-sm text-gray-500'>{t('dns.nacitam')}</div> : (
            <div className='space-y-3'>
              <div className='flex items-center gap-3'>
                <StatusDot status={localRunning ? (adminAlive ? 'running' : 'pending') : 'stopped'} />
                <div className='flex-1'>
                  <div className='font-medium text-sm'>
                    {localRunning
                      ? (adminAlive
                        ? t('dns.statusRunning', 'Běží')
                        : t('dns.statusStarting', 'Startuje (admin API neodpovídá)'))
                      : t('dns.statusOff', 'Vypnuto')}
                  </div>
                  <div className='text-xs text-gray-500 font-mono'>
                    {s.local.container_status ?? 'no container'}
                  </div>
                </div>
                <div className='flex gap-2'>
                  {!localRunning && (
                    <Button variant='primary' size='sm' onClick={() => setEnableOpen(true)}>
                      <Power size={14} /> Enable
                    </Button>
                  )}
                  {localRunning && (
                    <>
                      <a href={s.local.admin_url_local} target='_blank' rel='noreferrer'>
                        <Button variant='secondary' size='sm'>{t('dns.adminUi')}</Button>
                      </a>
                      <Button variant='danger' size='sm' onClick={() => setDisableOpen(true)}>
                        <Power size={14} /> Disable
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {localRunning && (
                <div className='grid grid-cols-2 gap-3 text-xs'>
                  <div><span className='text-gray-500'>{t('dns.dnsPort')}</span> <code>{s.local.dns_port ?? '—'}</code></div>
                  <div><span className='text-gray-500'>{t('dns.publicIp')}</span> <code>{s.local.public_ip ?? '—'}</code></div>
                  <div><span className='text-gray-500'>{t('dns.adminUrl')}</span> <code>{s.local.admin_url_local}</code></div>
                  <div><span className='text-gray-500'>{t('dns.vMeshKatalogu')}</span> {s.local.advertised ? <Badge variant='green'>ano</Badge> : <Badge variant='yellow'>ne</Badge>}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Globe} className='text-sm'>DNS servery v mesh ({s?.mesh.count ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!s?.mesh.servers.length ? (
            <EmptyState
              icon={<Server size={32} />}
              title={t('dns.zadneDnsServeryV')}
              description={t('dns.spustDnsServerLokalne')}
            />
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='text-xs text-gray-500 border-b border-gray-800'>
                  <tr>
                    <th className='text-left p-2'>Node</th>
                    <th className='text-left p-2'>{t('dns.publicIp2')}</th>
                    <th className='text-left p-2'>{t('dns.dnsPort2')}</th>
                    <th className='text-left p-2'>{t('dns.upstreamUrlUdp')}</th>
                    <th className='text-left p-2'>Engine</th>
                    <th className='text-right p-2'>Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {s.mesh.servers.map((srv) => (
                    <tr key={srv.node_id} className='border-b border-gray-800/40 last:border-0'>
                      <td className='p-2 font-mono text-gray-200'>{srv.hostname ?? srv.node_id}</td>
                      <td className='p-2 font-mono text-gray-400'>{srv.public_ip ?? '—'}</td>
                      <td className='p-2 font-mono text-gray-400'>{srv.dns_port ?? '—'}</td>
                      <td className='p-2 font-mono text-xs text-gray-400'>{srv.dns_url_udp ?? '—'}</td>
                      <td className='p-2'><Badge variant='gray'>{srv.engine ?? '?'}</Badge></td>
                      <td className='p-2'>
                        <div className='flex justify-end'>
                          {srv.dns_url_udp && (
                            <Button
                              variant='ghost'
                              size='icon'
                              title={t('dns.zkopirovatUpstreamUrl')}
                              onClick={() => {
                                navigator.clipboard.writeText(srv.dns_url_udp!).catch(() => {});
                                toast.success(t('dns.urlZkopirovana'));
                              }}
                            >
                              <Copy size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={enableOpen} onClose={() => setEnableOpen(false)} title={t('dns.spustitLokalniDnsServer')} size='md'>
        <div className='space-y-3'>
          <div className='text-sm text-gray-600'>
            {t(
              'dns.enableAdguardHint',
              'Nahodí AdGuard Home jako Docker container. Inzeruje se v mesh katalogu, peer nody ho mohou použít jako upstream resolver nebo pro ACME DNS-01 challenge.',
            )}
          </div>
          <div>
            <label className='text-xs font-medium text-gray-500 block mb-1'>{t('dns.dnsPortUdpTcp')}</label>
            <input
              type='number'
              value={dnsPort}
              onChange={(e) => setDnsPort(Number(e.target.value) || 53)}
              className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm'
            />
            <div className='text-xs text-gray-500 mt-1'>{t('dns.default53StandardDns')}</div>
          </div>
          <div className='flex justify-end gap-2 pt-2'>
            <Button variant='ghost' onClick={() => setEnableOpen(false)}>{t('dns.zrusit')}</Button>
            <Button variant='primary' onClick={() => enableMut.mutate(dnsPort)} disabled={enableMut.isPending}>
              {enableMut.isPending ? t('dns.starting', 'Spouštím…') : t('dns.start', 'Spustit')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={disableOpen} onClose={() => setDisableOpen(false)} title={t('dns.vypnoutDnsServer')} size='md'>
        <div className='space-y-3'>
          <div className='text-sm text-gray-600'>
            {t(
              'dns.disableAdguardHint',
              'Zastaví AdGuard Home container a smaže advertisement v katalogu. Konfigurace a filtry zůstávají na disku (znovuspuštění obnoví stav).',
            )}
          </div>
          <label className='flex items-center gap-2 text-sm'>
            <input type='checkbox' checked={purgeData} onChange={(e) => setPurgeData(e.target.checked)} />
            {t('dns.smazatIPersistentData')} <strong>{t('dns.destruktivni')}</strong>
          </label>
          <div className='flex justify-end gap-2 pt-2'>
            <Button variant='ghost' onClick={() => setDisableOpen(false)}>{t('dns.zrusit')}</Button>
            <Button variant='danger' onClick={() => disableMut.mutate(purgeData)} disabled={disableMut.isPending}>
              {disableMut.isPending ? t('dns.stopping', 'Vypínám…') : t('dns.stop', 'Vypnout')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Tenant Domains Tab (allowlist pro publish) ────────────────────────────
interface TenantDomain {
  tenant_id: string;
  tenant_slug: string | null;
  domain: string;
  is_primary: boolean;
  created_at: string;
}

interface Tenant {
  id?: string;
  slug: string;
  name?: string;
  label?: string | null;
}

interface ProviderDomainRow {
  domain: string;
  provider_name: string;
  provider_type: string;
  source: 'live' | 'config' | 'local' | 'tenant';
  expires?: string | null;
  status?: string | null;
  zone_id?: string | null;
  tenant_slug?: string | null;
  is_primary?: boolean;
  error?: string;
}

export function DnsDomainsWidget(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scope = _props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';
  const [addModal, setAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newTenant, setNewTenant] = useState('');
  const [newPrimary, setNewPrimary] = useState(false);
  const [verifyRequired, setVerifyRequired] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  // Katalog všech domén z providerů
  const [catalogQ, setCatalogQ] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');

  // Live katalog — parallel API na providery (může trvat); staleTime delší
  const catalogQuery = useQuery({
    queryKey: ['dns-provider-domains', nodeKey],
    queryFn: () =>
      callWidgetTool<{
        ok: boolean;
        total?: number;
        domains?: ProviderDomainRow[];
        providers?: Array<{
          name: string;
          type: string;
          ok: boolean;
          error?: string | null;
          ms: number;
          count: number;
        }>;
        note?: string;
      }>('dns.provider_domains', {}, scope),
    staleTime: 60_000,
    retry: 1,
  });
  const catalogAll = catalogQuery.data?.domains ?? [];
  const providerStats = catalogQuery.data?.providers ?? [];

  const catalogFiltered = useMemo(() => {
    const q = catalogQ.trim().toLowerCase();
    return catalogAll.filter((d) => {
      if (filterProvider && d.provider_name !== filterProvider) return false;
      if (filterType && d.provider_type !== filterType) return false;
      if (filterSource && d.source !== filterSource) return false;
      if (!q) return true;
      return (
        d.domain.includes(q) ||
        d.provider_name.toLowerCase().includes(q) ||
        d.provider_type.toLowerCase().includes(q) ||
        (d.tenant_slug ?? '').toLowerCase().includes(q)
      );
    });
  }, [catalogAll, catalogQ, filterProvider, filterType, filterSource]);

  const providerNames = useMemo(
    () => [...new Set(catalogAll.map((d) => d.provider_name))].sort(),
    [catalogAll],
  );
  const providerTypes = useMemo(
    () => [...new Set(catalogAll.map((d) => d.provider_type))].sort(),
    [catalogAll],
  );

  const domainsQ = useQuery({
    queryKey: ['dns-domains', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; count: number; domains: TenantDomain[] }>(
        'dns.domain_list',
        {},
        scope,
      ),
    staleTime: 15_000,
  });

  const tenantsQ = useQuery({
    queryKey: ['tenants', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; tenants: Tenant[] }>('sso.list_tenants', {}, scope),
    staleTime: 60_000,
  });

  const registerMut = useMutation({
    mutationFn: (payload: {
      domain: string;
      tenant_slug: string;
      is_primary?: boolean;
      require_txt_verify?: boolean;
      verify_token?: string;
    }) =>
      callWidgetTool<{ ok: boolean; error?: string; domain?: string }>(
        'dns.domain_register',
        payload,
        scope,
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('dns.chyba'));
        return;
      }
      toast.success(t('dns.domenaZaregistrovana', { domain: r.domain }));
      qc.invalidateQueries({ queryKey: ['dns-domains', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-provider-domains', nodeKey] });
      setAddModal(false);
      setNewDomain('');
      setVerifyToken('');
      setVerifyRequired(false);
      setNewPrimary(false);
    },
  });

  const unregisterMut = useMutation({
    mutationFn: (domain: string) =>
      callWidgetTool<{ ok: boolean; error?: string; published_urls?: string[] }>(
        'dns.domain_unregister',
        { domain },
        scope,
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        const detail = r.published_urls?.length ? `\n${r.published_urls.join('\n')}` : '';
        toast.error((r.error ?? t('dns.chyba')) + detail);
        return;
      }
      toast.success(t('dns.domenaOdregistrovana'));
      qc.invalidateQueries({ queryKey: ['dns-domains', nodeKey] });
      qc.invalidateQueries({ queryKey: ['dns-provider-domains', nodeKey] });
    },
  });

  const domains = domainsQ.data?.domains ?? [];
  const tenants = tenantsQ.data?.tenants ?? [];

  // Group by tenant for cleaner display
  const grouped = new Map<string, { tenant_slug: string | null; tenant_id: string; domains: TenantDomain[] }>();
  for (const d of domains) {
    const k = d.tenant_slug ?? d.tenant_id;
    if (!grouped.has(k)) grouped.set(k, { tenant_slug: d.tenant_slug, tenant_id: d.tenant_id, domains: [] });
    grouped.get(k)!.domains.push(d);
  }

  const sourceBadge = (s: string) => {
    if (s === 'live') return <Badge variant='green'>live</Badge>;
    if (s === 'tenant') return <Badge variant='blue'>tenant</Badge>;
    if (s === 'local') return <Badge variant='yellow'>local</Badge>;
    return <Badge variant='gray'>config</Badge>;
  };

  return (
    <div className='space-y-4 p-1'>
      {/* ── Katalog ze všech providerů ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle icon={Globe}>
              {t('dns.katalogDomen')}
            </CardTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('dns.katalogHint')}
            </p>
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            {catalogQuery.data?.total != null && (
              <Badge variant='blue'>{catalogFiltered.length}/{catalogQuery.data.total}</Badge>
            )}
            <RefreshButton
              onRefresh={() => catalogQuery.refetch()}
              loading={catalogQuery.isFetching}
            />
          </div>
        </CardHeader>
        <CardContent className='space-y-3 min-w-0'>
          <div className='flex flex-col sm:flex-row gap-2 min-w-0'>
            <SearchInput
              value={catalogQ}
              onChange={setCatalogQ}
              size='md'
              className='flex-1 min-w-0'
              placeholder={t('dns.hledatDomenu')}
              resultCount={catalogFiltered.length}
              totalCount={catalogAll.length}
            />
            <Select
              className='sm:w-44'
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
            >
              <option value=''>{t('dns.vsichniProvidery')}</option>
              {providerNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
            <Select
              className='sm:w-36'
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value=''>{t('dns.vsechnyTypy')}</option>
              {providerTypes.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
            <Select
              className='sm:w-32'
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
            >
              <option value=''>{t('dns.vseZdroje')}</option>
              <option value='live'>live</option>
              <option value='config'>config</option>
              <option value='local'>local</option>
              <option value='tenant'>tenant</option>
            </Select>
          </div>

          {providerStats.length > 0 && (
            <div className='flex flex-wrap gap-1.5 text-[10px]'>
              {providerStats.map((p) => (
                <span
                  key={p.name}
                  className={`rounded-full border px-2 py-0.5 font-mono ${
                    p.ok
                      ? 'border-emerald-500/30 text-emerald-300/90'
                      : 'border-red-500/30 text-red-300'
                  }`}
                  title={p.error ?? `${p.ms} ms`}
                >
                  {p.name}: {p.ok ? `${p.count} (${p.ms}ms)` : `err ${p.ms}ms`}
                </span>
              ))}
            </div>
          )}

          {catalogQuery.isLoading && (
            <div className='text-sm text-gray-500 py-6 text-center'>
              {t('dns.nacitamProvidery')}
            </div>
          )}
          {catalogQuery.isError && (
            <div className='text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl p-3'>
              {(catalogQuery.error as Error)?.message ?? t('dns.katalogNacteniSelhalo')}
            </div>
          )}
          {!catalogQuery.isLoading && catalogFiltered.length === 0 && (
            <EmptyState
              icon={<Globe size={24} />}
              title={t('dns.zadneDomenyVKatalogu')}
              description={
                catalogAll.length
                  ? t('dns.filtrNic')
                  : t('dns.katalogPrazdny')
              }
            />
          )}
          {catalogFiltered.length > 0 && (
            <>
              <div className='md:hidden max-h-[min(28rem,55vh)] overflow-auto space-y-2'>
                {catalogFiltered.map((d) => (
                  <div
                    key={`${d.provider_name}|${d.domain}|${d.source}`}
                    className='rounded-xl border border-gray-800 bg-gray-950/50 p-2.5 space-y-1.5'
                  >
                    <div className='font-mono text-xs text-gray-100 break-all'>{d.domain}</div>
                    <div className='flex flex-wrap items-center gap-1.5'>
                      <span className='text-[11px] text-gray-400'>{d.provider_name}</span>
                      <Badge variant='gray'>{d.provider_type}</Badge>
                      {sourceBadge(d.source)}
                    </div>
                    {(d.expires || d.status || d.tenant_slug || d.error) && (
                      <div className='text-[10px] text-gray-500 break-all'>
                        {d.expires && <span title={t('dns.expirace')}>exp {d.expires.slice(0, 10)}</span>}
                        {d.status && <span className='ml-1'>{d.status}</span>}
                        {d.tenant_slug && <span className='ml-1'>@{d.tenant_slug}</span>}
                        {d.error && (
                          <span className='text-red-400/80 ml-1' title={d.error}>err</span>
                        )}
                      </div>
                    )}
                    <div className='flex gap-1 pt-0.5'>
                      <Button
                        variant='secondary'
                        size='sm'
                        className='flex-1 justify-center'
                        title={t('dns.otevritZonu')}
                        onClick={() => {
                          const url = new URL(window.location.href);
                          url.searchParams.set('tab', 'zones');
                          url.searchParams.set('zone', d.domain);
                          if (d.provider_name && d.provider_name !== 'tenant') {
                            url.searchParams.set('provider', d.provider_name);
                          }
                          window.location.assign(url.toString());
                        }}
                      >
                        <Search size={12} /> {t('dns.otevritZonu')}
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        title={t('dns.registrovatTenantovi')}
                        onClick={() => {
                          setNewDomain(d.domain);
                          setAddModal(true);
                        }}
                      >
                        <Plus size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className='hidden md:block max-h-[min(28rem,55vh)] overflow-auto border border-gray-800 rounded-xl'>
                <table className='w-full text-xs'>
                  <thead className='text-gray-500 border-b border-gray-800 sticky top-0 bg-gray-950 z-[1]'>
                    <tr>
                      <th className='text-left p-2'>{t('dns.domena')}</th>
                      <th className='text-left p-2'>{t('dns.provider')}</th>
                      <th className='text-left p-2'>{t('dns.typ')}</th>
                      <th className='text-left p-2'>{t('dns.zdroj')}</th>
                      <th className='text-left p-2'>{t('dns.info')}</th>
                      <th className='text-right p-2'>{t('dns.akce')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogFiltered.map((d) => (
                      <tr
                        key={`${d.provider_name}|${d.domain}|${d.source}`}
                        className='border-b border-gray-800/40 last:border-0 hover:bg-gray-900/40'
                      >
                        <td className='p-2 font-mono text-gray-100'>{d.domain}</td>
                        <td className='p-2 text-gray-400'>{d.provider_name}</td>
                        <td className='p-2'>
                          <Badge variant='gray'>{d.provider_type}</Badge>
                        </td>
                        <td className='p-2'>{sourceBadge(d.source)}</td>
                        <td className='p-2 text-gray-500'>
                          {d.expires && <span title={t('dns.expirace')}>exp {d.expires.slice(0, 10)}</span>}
                          {d.status && <span className='ml-1'>{d.status}</span>}
                          {d.tenant_slug && <span className='ml-1'>@{d.tenant_slug}</span>}
                          {d.error && (
                            <span className='text-red-400/80 ml-1' title={d.error}>err</span>
                          )}
                        </td>
                        <td className='p-2 text-right'>
                          <Button
                            variant='ghost'
                            size='sm'
                            title={t('dns.otevritZonu')}
                            onClick={() => {
                              const url = new URL(window.location.href);
                              url.searchParams.set('tab', 'zones');
                              url.searchParams.set('zone', d.domain);
                              if (d.provider_name && d.provider_name !== 'tenant') {
                                url.searchParams.set('provider', d.provider_name);
                              }
                              window.location.assign(url.toString());
                            }}
                          >
                            <Search size={12} />
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            title={t('dns.registrovatTenantovi')}
                            onClick={() => {
                              setNewDomain(d.domain);
                              setAddModal(true);
                            }}
                          >
                            <Plus size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Allowlist per tenant ─────────────────────────────────────────── */}
      <Card>
        <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
          <div className='flex items-center justify-between gap-2 w-full min-w-0'>
            <CardTitle>{t('dns.domenyPerTenant')}</CardTitle>
            <Button size='sm' icon={<Plus size={14} />} onClick={() => setAddModal(true)} className='shrink-0'>
              {t('dns.registrovatDomenu')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className='min-w-0'>
          <div className='text-xs text-gray-400 mb-3'>
            {t('dns.allowlistDomenPro')} <code className='font-mono text-blue-300'>network.publish</code>.
            {' '}{t('dns.allowlistHint')}
          </div>
          {domainsQ.isLoading && <div className='text-gray-500 text-sm'>{t('dns.nacitam')}</div>}
          {domains.length === 0 && !domainsQ.isLoading && (
            <EmptyState icon={<Globe size={24} />} title={t('dns.zatimZadneRegistrovaneDomeny')} />
          )}
          <div className='space-y-3'>
            {[...grouped.values()].map((g) => (
              <div key={g.tenant_id} className='border border-gray-800 rounded-lg p-3'>
                <div className='text-xs uppercase font-bold text-gray-400 mb-2'>
                  {g.tenant_slug ?? g.tenant_id.slice(0, 8)}
                </div>
                <div className='space-y-1.5'>
                  {g.domains.map((d) => (
                    <div
                      key={d.domain}
                      className='flex flex-col xs:flex-row sm:items-center sm:justify-between gap-1.5 bg-gray-950 rounded px-2 py-1.5'
                    >
                      <div className='flex items-center gap-2 text-sm min-w-0'>
                        {d.is_primary && <Star size={12} className='text-yellow-400 fill-yellow-400 shrink-0' />}
                        <span className='font-mono text-gray-200 break-all'>{d.domain}</span>
                        {d.is_primary && <Badge>primary</Badge>}
                      </div>
                      <div className='flex items-center gap-1.5 shrink-0 self-end sm:self-auto'>
                        <span className='text-[10px] text-gray-500'>
                          {new Date(d.created_at).toLocaleDateString()}
                        </span>
                        <button
                          className='text-gray-500 hover:text-red-400 p-1'
                          title={t('dns.odregistrovat')}
                          onClick={async () => {
                            if (d.is_primary) {
                              toast.error(t('dns.nejdrivOdeberPrimaryFlag'));
                              return;
                            }
                            if (await themedConfirm(t('dns.odregistrovatConfirm', { domain: d.domain }))) {
                              unregisterMut.mutate(d.domain);
                            }
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {addModal && (
        <Modal open onClose={() => setAddModal(false)} title={t('dns.registrovatDomenuTenantovi')}>
          <div className='space-y-3'>
            <Field label={t('dns.domenaFqdn')} value={newDomain} onChange={setNewDomain} mono />
            <label className='block text-xs'>
              <span className='text-gray-400 mb-1 block'>{t('dns.tenant')}</span>
              <Select
                className='w-full'
                value={newTenant}
                onChange={(e) => setNewTenant(e.target.value)}
              >
                <option value=''>{t('dns.vyber')}</option>
                {tenants.filter((tt) => tt.slug !== 'global').map((tt) => (
                  <option key={tt.slug} value={tt.slug}>
                    {tt.slug}{tt.name && tt.name !== tt.slug ? ` — ${tt.name}` : ''}
                  </option>
                ))}
              </Select>
            </label>
            <label className='flex items-center gap-2 text-xs text-gray-400'>
              <input type='checkbox' checked={newPrimary} onChange={(e) => setNewPrimary(e.target.checked)} />
              {t('dns.nastavitJakoPrimarniDomenu')}
            </label>
            <label className='flex items-center gap-2 text-xs text-gray-400'>
              <input type='checkbox' checked={verifyRequired} onChange={(e) => setVerifyRequired(e.target.checked)} />
              {t('dns.overitVlastnictviPresTxt')}
            </label>
            {verifyRequired && (
              <div className='space-y-2'>
                <Field
                  label={t('dns.txtTokenLabel', 'TXT token (vytvoř _acm-verification.{{domain}})', {
                    domain: newDomain || t('dns.domainPlaceholder', '<doména>'),
                  })}
                  value={verifyToken}
                  onChange={setVerifyToken}
                  mono
                />
              </div>
            )}
            <div className='flex justify-end gap-2 pt-2 border-t border-gray-800'>
              <Button variant='secondary' onClick={() => setAddModal(false)}>{t('dns.zrusit')}</Button>
              <Button
                variant='primary'
                loading={registerMut.isPending}
                disabled={!newDomain || !newTenant || (verifyRequired && !verifyToken)}
                onClick={() =>
                  registerMut.mutate({
                    domain: newDomain,
                    tenant_slug: newTenant,
                    is_primary: newPrimary,
                    require_txt_verify: verifyRequired,
                    verify_token: verifyRequired ? verifyToken : undefined,
                  })}
              >
                Registrovat
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Widget: dns.templates — šablony zón + apply */
export function DnsTemplatesWidget(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scope = _props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';
  const [domain, setDomain] = useState('');
  const [aIp, setAIp] = useState('');
  const [applySlug, setApplySlug] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['dns-templates', nodeKey],
    queryFn: () =>
      callWidgetTool<{
        ok: boolean;
        templates?: Array<{
          slug: string;
          title: string;
          description: string | null;
          builtin: boolean;
          record_count: number;
        }>;
      }>('dns.zone_template', { action: 'list' }, scope),
  });
  const templates = listQ.data?.templates ?? [];

  const applyMut = useMutation({
    mutationFn: (slug: string) =>
      callWidgetTool<{
        ok: boolean;
        error?: string;
        created?: string[];
        errors?: string[];
        note?: string;
      }>('dns.zone_template', {
        action: 'apply',
        slug,
        domain,
        vars: { A_IP: aIp, ACME_TOKEN: '' },
      }, scope),
    onSuccess: (r) => {
      if (!r.ok && r.errors?.length) {
        toast.error(r.errors[0] ?? r.error ?? t('dns.applySelhal'));
      } else if (!r.ok) {
        toast.error(r.error ?? t('dns.applySelhal'));
      } else {
        toast.success(
          `${t('dns.sablonaAplikovana', { count: (r.created ?? []).length })}${r.note ? ` ${r.note}` : ''}`,
        );
        qc.invalidateQueries({ queryKey: ['dns-records', nodeKey] });
      }
      setApplySlug(null);
    },
  });

  return (
    <div className='space-y-4 p-1 min-w-0'>
      <Card>
        <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
          <div className='min-w-0 flex-1'>
            <CardTitle icon={Wand2}>{t('dns.sablonyZon')}</CardTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('dns.sablonyHint')}
            </p>
          </div>
          <RefreshButton onRefresh={() => listQ.refetch()} loading={listQ.isFetching} />
        </CardHeader>
        <CardContent className='space-y-3 min-w-0'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
            <Field label={t('dns.cilovaZona')} value={domain} onChange={setDomain} mono />
            <Field label='A_IP (web-basic)' value={aIp} onChange={setAIp} mono />
          </div>
          {templates.length === 0 && !listQ.isLoading && (
            <EmptyState icon={<Wand2 size={24} />} title={t('dns.zadneSablony')} />
          )}
          <div className='space-y-2'>
            {templates.map((tpl) => (
              <div
                key={tpl.slug}
                className='flex flex-wrap items-center gap-2 rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2'
              >
                <div className='min-w-0 flex-1'>
                  <div className='text-sm text-gray-100 font-medium flex items-center gap-2'>
                    {tpl.title}
                    {tpl.builtin && <Badge variant='blue'>builtin</Badge>}
                    <span className='text-[10px] font-mono text-gray-500'>{tpl.slug}</span>
                  </div>
                  {tpl.description && (
                    <div className='text-xs text-gray-500 mt-0.5'>{tpl.description}</div>
                  )}
                  <div className='text-[10px] text-gray-600'>{tpl.record_count} {t('dns.zaznamu', 'záznamů')}</div>
                </div>
                <Button
                  size='sm'
                  variant='primary'
                  disabled={!domain}
                  loading={applyMut.isPending && applySlug === tpl.slug}
                  onClick={() => {
                    setApplySlug(tpl.slug);
                    applyMut.mutate(tpl.slug);
                  }}
                >
                  <Wand2 size={12} /> Apply
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const DEF = getModuleUi('dns');

export default function DnsPage() {
  if (!DEF) {
    return (
      <div className='p-6 text-sm text-red-300'>
        module-ui-catalog: dns missing — zkontroluj registry / catalog.
      </div>
    );
  }
  const tab = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('tab')
    : null;
  const allowed = new Set(DEF.tabs.map((x) => x.id));
  const defaultTab = tab && allowed.has(tab) ? tab : 'zones';
  return <ModuleWorkspace def={DEF} defaultTab={defaultTab} />;
}
